import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Crown, Layers, MapPin, Navigation, X } from "lucide-react";

// Fix default icon issue in Leaflet + webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom neon pin for Musas — red or gold depending on VIP status
function musaIcon(isVip) {
  const color = isVip ? "#D4AF37" : "#C8102E";
  const glow = isVip ? "rgba(212,175,55,0.75)" : "rgba(200,16,46,0.75)";
  const html = `
    <div style="
      width: 28px; height: 28px;
      background: ${color};
      border: 2px solid #0A0A0A;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 0 12px ${glow}, 0 2px 4px rgba(0,0,0,0.6);
      display: flex; align-items: center; justify-content: center;
    ">
      <div style="width: 8px; height: 8px; background: #0A0A0A; border-radius: 50%; transform: rotate(45deg);"></div>
    </div>
  `;
  return L.divIcon({
    html,
    className: "pk2-musa-marker",
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

function FitToProfiles({ profiles, userPos }) {
  const map = useMap();
  useEffect(() => {
    const points = profiles
      .filter((p) => p.lat && p.lng)
      .map((p) => [p.lat, p.lng]);
    if (userPos) points.push([userPos.lat, userPos.lng]);
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
    }
  }, [profiles, userPos, map]);
  return null;
}

export default function NearbyMap({ profiles = [], userPos = null, onRecenter }) {
  const [layer, setLayer] = useState("street"); // street | topo
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);

  const center = useMemo(() => {
    if (userPos) return [userPos.lat, userPos.lng];
    if (profiles.length && profiles[0].lat) return [profiles[0].lat, profiles[0].lng];
    return [23.6345, -102.5528]; // fallback: center of Mexico
  }, [userPos, profiles]);

  const tiles = layer === "topo"
    ? {
        url: "https://tile.opentopomap.org/{z}/{x}/{y}.png",
        attribution: 'Map data: © OpenStreetMap contributors · © OpenTopoMap',
        maxZoom: 17,
      }
    : {
        url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      };

  return (
    <div
      className="relative w-full"
      style={{ height: "calc(100vh - 160px)", minHeight: "520px", background: "var(--bg-surface)" }}
      data-testid="nearby-map-wrapper"
    >
      <MapContainer
        center={center}
        zoom={userPos ? 12 : 5}
        style={{ height: "100%", width: "100%", background: "#0A0A0A" }}
        scrollWheelZoom
        whenReady={(e) => { mapRef.current = e.target; }}
      >
        <TileLayer key={layer} url={tiles.url} attribution={tiles.attribution} maxZoom={tiles.maxZoom} />

        <FitToProfiles profiles={profiles} userPos={userPos} />

        {userPos && (
          <CircleMarker
            center={[userPos.lat, userPos.lng]}
            radius={10}
            pathOptions={{ color: "#2D8CFF", fillColor: "#2D8CFF", fillOpacity: 0.95, weight: 3 }}
            data-testid="user-location-marker"
          />
        )}
        {userPos && userPos.accuracy && (
          <CircleMarker
            center={[userPos.lat, userPos.lng]}
            radius={Math.min(Math.max(userPos.accuracy / 20, 20), 80)}
            pathOptions={{ color: "#2D8CFF", fillColor: "#2D8CFF", fillOpacity: 0.1, weight: 1 }}
          />
        )}

        {profiles.filter((p) => p.lat && p.lng).map((p) => (
          <Marker
            key={p.id}
            position={[p.lat, p.lng]}
            icon={musaIcon(p.is_vip)}
            eventHandlers={{ click: () => setSelected(p) }}
            data-testid={`map-pin-${p.id}`}
          />
        ))}
      </MapContainer>

      {/* Top-right controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2" style={{ zIndex: 1000 }}>
        <div
          className="flex p-1"
          style={{ background: "rgba(10,10,10,0.92)", border: "1px solid var(--brand-primary)", backdropFilter: "blur(8px)" }}
          data-testid="map-layer-toggle"
        >
          <button
            onClick={() => setLayer("street")}
            className="px-3 py-2 text-xs uppercase tracking-[0.15em] transition-colors"
            style={{
              background: layer === "street" ? "var(--brand-primary)" : "transparent",
              color: layer === "street" ? "#fff" : "var(--text-secondary)",
            }}
            data-testid="map-layer-street"
          >
            Mapa
          </button>
          <button
            onClick={() => setLayer("topo")}
            className="px-3 py-2 text-xs uppercase tracking-[0.15em] transition-colors inline-flex items-center gap-1"
            style={{
              background: layer === "topo" ? "var(--brand-primary)" : "transparent",
              color: layer === "topo" ? "#fff" : "var(--text-secondary)",
            }}
            data-testid="map-layer-topo"
          >
            <Layers size={12} strokeWidth={1.4} /> Topográfico
          </button>
        </div>

        {userPos && (
          <button
            onClick={() => {
              if (mapRef.current) mapRef.current.setView([userPos.lat, userPos.lng], 14);
              onRecenter && onRecenter();
            }}
            className="flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.15em]"
            style={{
              background: "rgba(10,10,10,0.92)",
              border: "1px solid var(--border-accent)",
              color: "var(--text-primary)",
              backdropFilter: "blur(8px)",
            }}
            data-testid="map-recenter-btn"
          >
            <Navigation size={12} strokeWidth={1.4} /> Centrarme
          </button>
        )}
      </div>

      {/* Side card for selected profile */}
      {selected && (
        <div
          className="absolute bottom-4 left-4 right-4 md:right-auto md:w-[360px] p-4"
          style={{
            background: "rgba(10,10,10,0.96)",
            border: "1px solid var(--brand-primary)",
            backdropFilter: "blur(12px)",
            zIndex: 1000,
            boxShadow: "0 8px 40px rgba(200,16,46,0.25)",
          }}
          data-testid={`map-selected-card-${selected.id}`}
        >
          <button
            onClick={() => setSelected(null)}
            className="absolute top-2 right-2 p-1"
            style={{ color: "var(--text-muted)" }}
            data-testid="map-card-close"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
          <div className="flex gap-3">
            <img
              src={selected.hero_photo || (selected.photos && selected.photos[0]) || "https://images.unsplash.com/photo-1506863530036-1efeddceb993?w=400&q=60"}
              alt={selected.stage_name}
              className="object-cover"
              style={{ width: "96px", height: "120px", background: "var(--bg-elevated)" }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-serif text-lg truncate" style={{ color: "var(--text-primary)" }}>
                  {selected.stage_name}
                </span>
                {selected.is_vip && <Crown size={14} strokeWidth={1.3} color="var(--vip-gold)" />}
              </div>
              <p className="text-xs mb-2 inline-flex items-center gap-1" style={{ color: "var(--text-secondary)" }}>
                <MapPin size={10} /> {selected.city}
                {selected.distance_km != null && (
                  <span style={{ color: "var(--brand-primary)", marginLeft: 6 }}>· {selected.distance_km} km</span>
                )}
              </p>
              {selected.age && (
                <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
                  {selected.age} años
                </p>
              )}
              <Link
                to={`/musa/${selected.slug || selected.id}`}
                className="btn-primary text-xs inline-block"
                data-testid={`map-view-profile-${selected.id}`}
              >
                Ver perfil →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
